import { forwardRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

import { breakpoints } from 'Components/ThemeProvider';
import ConsultationCTA from 'Containers/ConsultationCTA';
import Typography from 'Components/Typography';
import Spacer from 'Components/Spacer';
import Button from 'Components/LegacyButton';

import { heroContent as heroContentDI } from 'assets/content/home';

const Section = styled.section`
  ${({ theme }) => `
    display: grid;
    grid-template-areas: "hero";

    min-height: calc(100vh - ${theme.props.headerHeight}px);
    width: 100vw;
  `}
`;

const Content = styled.div`
  ${({ theme }) => `
  grid-area: hero;

  width: 100%;
  max-width: 100vw;

  display: flex;
  flex-flow: column nowrap;
  align-items: center;

  padding-top: ${theme.spacing.s48}px;

  padding-left: ${theme.spacing.s24}px;
  padding-right: ${theme.spacing.s24}px;

  ${theme.breakpoints.up('md')} {
    justify-content: center;
    padding: 0 0 0 50%;
  }
  `}
`;

const Picture = styled.picture`
  ${({ theme }) => `
    grid-area: hero;

    object-fit: cover;
    height: calc(100vh - ${theme.props.headerHeight}px);
    width: 100%;
  `}
`;

const Image = styled.img`
  ${({ theme }) => `
    grid-area: hero;

    object-fit: cover;
    height: calc(100vh - ${theme.props.headerHeight}px);
    width: 100%;
  `}
`;

const Title = styled(Typography)`
  ${({ theme }) => `
    font-size: 34px;
    max-width: 550px;
    ${theme.breakpoints.up('xs')} {
        font-size: 36px;
    }
    ${theme.breakpoints.up('sm')} {
        font-size: 48px;
    }
  `}
`;

const HomeHero = forwardRef(({ heroContent, showCTA }, ref) => {
  const {
    title,
    subtitle,
    cta,
    darkImage,
    backgroundMobile,
    backgroundTablet,
    backgroundDesktop,
  } = heroContent();
  return (
    <Section ref={ref} data-testid="home-hero">
      <Picture>
        <source media={`(max-width: ${breakpoints.sm - 1}px)`} srcSet={backgroundMobile} />
        <source media={`(max-width: ${breakpoints.md - 1}px)`} srcSet={backgroundTablet} />
        <source media={`(min-width: ${breakpoints.md}px)`} srcSet={backgroundDesktop} />
        <Image
          alt="Prose pre shampoo hair mask, shampoo, conditioner and supplements on soaps bars"
          src={backgroundDesktop}
          srcSet={`${backgroundMobile} 750w,
                ${backgroundTablet} 1536w,
                ${backgroundDesktop} 2880w`}
        />
      </Picture>
      <Content>
        <Title
          align="center"
          color={darkImage ? 'white' : 'noirDark'}
          data-testid="home-hero-title"
          variant="hero2"
        >
          {title}
        </Title>
        <Spacer size={24} />
        {subtitle && (
          <Typography align="center" color={darkImage ? 'white' : 'noirDark'} variant="p2">
            {subtitle}
          </Typography>
        )}
        <Spacer size={24} />
        {showCTA && cta.component === 'Cta' && (
          <ConsultationCTA
            css={{
              minWidth: '279px',
              maxWidth: '279px',
            }}
            data-click="consultation"
            data-id="home-hero-cta"
            data-testid="home-hero-cta"
            dataFrom="hero"
            label={cta.label}
            variant="vertFull"
          />
        )}
        {cta.component === 'link' && (
          <Button
            aria-label={cta.label}
            Component={Link}
            css={{
              minWidth: '279px',
              maxWidth: '279px',
            }}
            data-click={cta.heapClick}
            data-from="hero"
            to={cta.link}
            variant="vertFull"
          >
            {cta.label}
          </Button>
        )}
      </Content>
    </Section>
  );
});

HomeHero.defaultProps = {
  showCTA: true,
  heroContent: heroContentDI,
};

HomeHero.propTypes = {
  showCTA: PropTypes.bool,
  heroContent: PropTypes.func,
};

export default connect(null, null, null, { forwardRef: true })(HomeHero);
